/**
 * `RosterHistory` — the per-actor undo/redo authority for the roster state
 * domain: a **thin binding** of the generic `History<S, Cmd, Event>`
 * (`./commandHistory.ts`). It contributes exactly two things on top of the
 * generic: it binds `applyRoster` (threading a read-only `Compendium` into every
 * reducer call as `ctx.compendium`) and it holds the
 * `(state, compendium, history)` constructor shape its drivers call.
 *
 * **The undo machinery lives once, in `History`** — this binding is the
 * roster-shaped view of it, imported by every driver rather than re-implemented
 * per driver.
 *
 * It is I/O-free (no filesystem, no IndexedDB, no websocket): the generic holds
 * the raw `RosterState` + per-actor undo/redo stacks and routes every change
 * through the bound `applyRoster(state, command, ctx)` reducer. Persistence and
 * transport live in the drivers one layer up.
 *
 * See `./commandHistory.ts` for the per-actor / reject-stale-as-no-op semantics.
 */
import { applyRoster, type RosterEvent } from "./reducer";
import type { RosterCommand } from "./commands";
import type { RosterState } from "./types";
import type { Compendium } from "@lancer-vtt/compendium";
import { History, type ActorStacks, type UndoState } from "./commandHistory";

/** Undo/redo availability for one actor (the generic {@link UndoState}). */
export type RosterUndoState = UndoState;

/** One actor's undo + redo stacks of roster commands. */
export type RosterActorStacks = ActorStacks<RosterCommand>;

/**
 * The two roster scope-string constructors. The format lives
 * here ONCE so the server's `scopeOfRosterCommand` and the client (the editors that
 * name the scope on undo/redo, the integration tests) can never diverge — a window
 * names its scope with the same helper that the server uses to file the command's
 * inverse. A `mech:` build edit and a `pilot:` record edit are the two per-subject
 * scopes; {@link encounterCommitScope} is the third, for a whole encounter commit.
 */
export function mechScope(mechId: string): string {
  return `mech:${mechId}`;
}

/** The pilot-record / mech-lifecycle scope string. See {@link mechScope}. */
export function pilotScope(pilotId: string): string {
  return `pilot:${pilotId}`;
}

/**
 * The scope an encounter commit's undo lives in: the commit batch spans several pilots'
 * mechs, so it files under the encounter it committed rather than any one pilot.
 */
export function encounterCommitScope(encounterId: string): string {
  return `encounter-commit:${encounterId}`;
}

/** The scope the GM's FULL REPAIR SQUAD (`full_repair_squad`) undoes in: it spans every lancer. */
export function squadRepairScope(): string {
  return "squad-repair";
}

/** The scope a keyed NPC's wear edits (`apply_npc_wear` / `clear_npc_wear`) undo in. */
export function npcWearScope(key: string): string {
  return `npc-wear:${key}`;
}

/**
 * The roster scope rule: which undo sub-partition a roster command lives in,
 * derived purely from the command. Four scopes:
 *
 *  - **`mech:<mechId>`** — a mech *build* edit, so an UNDO in an open MECH.app only
 *    walks back that mech's loadout: `rename_mech`, `select_frame`,
 *    `equip_weapon`/`unequip_weapon`, `equip_system`/`unequip_system`,
 *    `attach_mod`/`detach_mod`, and `set_active_mech` (fired from MECH.app's
 *    ACTIVATE — undo-from-that-window is the natural gesture, even though the field
 *    it writes is pilot-level). `restore_loadout` (the inverse of `select_frame`)
 *    rides the same mech scope.
 *  - **`pilot:<pilotId>`** — everything else, the pilot-record edits owned by the
 *    PILOT editor: callsign/level/HASE/skills/talents/licenses/core-bonuses/gear,
 *    **and** the mech-roster *lifecycle* (`create_mech`/`delete_mech`, plus their
 *    inverse `restore_mech`). Lifecycle is deliberately pilot-scoped, NOT
 *    mech-scoped: `delete_mech` closes its own MECH.app (`onClose`), so a
 *    mech-scoped delete-undo would be stranded on a window that no longer exists —
 *    the pilot editor is the reachable home for undoing a roster change.
 *  - **`encounter-commit:<encounterId>`** — `record_encounter_commit` and the commit
 *    batch that carries it, so one undo reverts a whole multi-pilot commit.
 *  - **`npc-wear:<key>`** — a GM's direct edit of one keyed NPC's carried wear. Inside a
 *    commit batch they file under the commit's scope instead.
 *  - **`squad-repair`** — `full_repair_squad`, one undo for the whole squad's Full Repair.
 *
 * Inverses (`restore_*`, the re-equip/`set_*`-back commands) are never themselves
 * passed here during normal operation — they are popped from the stack the original
 * command's scope already chose — but the function stays total over `RosterCommand`
 * for robustness.
 */
export function scopeOfRosterCommand(command: RosterCommand): string {
  switch (command.type) {
    case "rename_mech":
    case "select_frame":
    case "equip_weapon":
    case "unequip_weapon":
    case "equip_system":
    case "unequip_system":
    case "attach_mod":
    case "detach_mod":
    case "set_active_mech":
    case "restore_loadout":
      return mechScope(command.mechId);
    case "restore_pilot":
      return pilotScope(command.pilot.id);
    case "record_encounter_commit":
      return encounterCommitScope(command.encounterId);
    case "apply_npc_wear":
    case "clear_npc_wear":
      return npcWearScope(command.key);
    case "full_repair_squad":
      return squadRepairScope();
    case "batch": {
      // An encounter commit batch (the one user-intent roster batch) files under its
      // encounter; any other batch is an inverse-composite, scoped by its first leaf.
      const record = command.commands.find((c) => c.type === "record_encounter_commit");
      if (record) return scopeOfRosterCommand(record);
      return command.commands.length > 0 ? scopeOfRosterCommand(command.commands[0]!) : "";
    }
    default:
      // Every remaining roster command is a pilot-record edit — including the
      // `create_mech`/`delete_mech`/`restore_mech` lifecycle — and carries `pilotId`.
      return pilotScope(command.pilotId);
  }
}

/**
 * In-memory roster authority: a thin {@link History} binding `applyRoster` with
 * an injected compendium ctx. All the undo/redo/snapshot machinery is inherited;
 * this class exists only to bind the reducer + ctx and hold the constructor
 * signature.
 */
export class RosterHistory extends History<RosterState, RosterCommand, RosterEvent> {
  /** The read-only compendium injected into every reducer call (never persisted/sent). */
  readonly compendium: Compendium;

  constructor(
    state: RosterState,
    compendium: Compendium,
    history: Record<string, RosterActorStacks> = {},
  ) {
    super((s, c) => applyRoster(s, c, { compendium }), state, history);
    this.compendium = compendium;
  }

  /**
   * Override the base's single-scope default with the roster mech-vs-pilot rule:
   * a mech build edit lands in `mech:<id>`, a pilot-record edit in `pilot:<id>`, so
   * MECH.app and the PILOT editor undo only their own subject.
   */
  override scopeOf(command: RosterCommand): string {
    return scopeOfRosterCommand(command);
  }
}
